import { format } from 'date-fns';
import { getUserFromLocalStorage, setUser } from './localstorage';
import { categoriesAndSubCategories } from '../constants/Category';

export const manipulateCategory = (category) => {
    return Object.groupBy(category, ({ category_id }) => category_id);
};

export const numberWithCommas = (x) => {
    return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatCurrency = (x) => {
    if (!x) {
        return '';
    }

    // Convert the value to a number first
    const numericValue = parseFloat(x);

    // If the conversion fails, return the original value
    if (isNaN(numericValue)) {
        return x;
    }
    const parts = numericValue.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
};

export const convertKeyToName = (ad) => {
    const keys = [
        'year',
        'color',
        'ad_condition',
        'property_type',
        'use',
        'size',
        'date_available',
        'facilities',
        'company_name',
        'service_type',
        'years',
        'mode_of_charge',
        'available_for_travel',
        'brand',
        'type',
        'material',
        'bulk_price',
        'gender',
        'age',
        'display_size',
        'style',
        'formulation',
        'scent_type',
        'platform',
        'format',
        'game_genre',
        'seller_type',
        'vehicle_specification',
        'make',
        'model',
        'vehicle_body',
        'vehicle_features',
        'transmission',
        'millage',
        'number_of_seat',
        'number_of_door',
        'fuel_type',
        'engine_size',
        'storage',
        'storage_type',
        'ram_size',
        'electronics_display_size',
        'processor',
        'operating_system',
        'resolution',
        'input_mode',
        'display_technology',
        'electronics_style',
        'connectivity',
        'connectivity_interface',
        'sim_type',
        'exchange_possible',
        'marketed_by',
        'furnished',
        'time_period',
        'room_bathroom',
        'employment_status',
        'expertise',
        'years_of_experience',
        'level_of_education',
        'employment_type',
        'display',
        'furniture_for',
        'service_area',
        'service_features',
        'payment_terms',
        'mode_of_transport',
        'form',
        'years_of_experience',
        'mode_of_charges',
        'release_year',
        'rating',
        'breed',
        'capacity',
    ];

    if (!ad) {
        return [];
    }

    const overviews = [];

    keys.forEach((key) => {
        if (!(key in ad)) {
            return;
        }

        const displayName = key
            .split('_')
            .map((val) => val[0].toUpperCase() + val.slice(1))
            .join(' ');

        let value = ad[key];

        // Handle "other" case
        if (value === 'other') {
            const otherKey = `${key}_other`;
            const otherValue = ad[otherKey];

            // Skip if no corresponding *_other field or it's empty
            if (!otherValue) {
                return;
            }

            value = otherValue;
        }

        // Skip if value is null, undefined, or empty string
        if (value === null || value === '') {
            return;
        }

        // Format date
        if (key === 'date_available') {
            value = format(new Date(value), 'PP');
        }

        overviews.push({ name: displayName, param: key, value });
    });

    return overviews;
};

export const arrayRange = (start, stop, step) =>
    Array.from({ length: (stop - start) / step + 1 }, (value, index) => start + index * step);

export const getStateLGA = (lga, state_id) => {
    const state_lga = lga.filter((val) => val.state_id === state_id);
    return state_lga;
};

export const removeNullObjectsValues = (productDetails) => {
    // convert the value to array, which return [key,value]
    const entries = Object.entries(productDetails);
    // filter the array
    const filteredEntries = entries.filter(([_key, value]) => value !== null && value !== '');
    // convert the array back to object
    return Object.fromEntries(filteredEntries);
};

export const manipulatePrice = (price, category_id) => {
    if (category_id.toString().startsWith('50')) {
        switch (price) {
            case '1500000': {
                const val = ['0', '1500000'];
                return val;
            }
            case '3000000':
                return ['1500000', '3000000'];
            case '7500000':
                return ['3000000', '7500000'];
            case '11000000':
                return ['7500000', '11000000'];
            case '>11000000':
                return '11000000';
            default:
                return '';
        }
    } else {
        switch (price) {
            case '5000': {
                const val = ['0', '5000'];
                return val;
            }
            case '10000':
                return ['5000', '10000'];
            case '15000':
                return ['10000', '15000'];
            case '20000':
                return ['15000', '20000'];
            case '30000':
                return ['20000', '30000'];
            case '>30000':
                return '30000';
            default:
                return '';
        }
    }
};
export const manipulateFilterForm = (values, category_id) => {
    // remove all the empty of null fields
    let filteredValue = removeNullObjectsValues(values);

    // Normalize min_price/max_price that may come formatted with commas for display
    if (filteredValue.min_price !== undefined) {
        const raw = filteredValue.min_price?.toString().replace(/[^0-9]/g, '');
        filteredValue.min_price = raw ? raw : '';
        if (filteredValue.min_price === '') {
            delete filteredValue.min_price;
        }
    }
    if (filteredValue.max_price !== undefined) {
        const raw = filteredValue.max_price?.toString().replace(/[^0-9]/g, '');
        filteredValue.max_price = raw ? raw : '';
        if (filteredValue.max_price === '') {
            delete filteredValue.max_price;
        }
    }

    if (filteredValue.min_price || filteredValue.max_price) {
        if ('price' in filteredValue) {
            delete filteredValue.price;
        }
    } else if ('price' in filteredValue) {
        const priceValue = filteredValue.price;
        const isEmptyPrice =
            priceValue === '' ||
            priceValue === null ||
            (Array.isArray(priceValue) && priceValue.length === 0);

        if (isEmptyPrice) {
            delete filteredValue.price;
        } else {
            filteredValue = {
                ...filteredValue,
                price: manipulatePrice(filteredValue.price, category_id),
            };
            if ('min_price' in filteredValue) {
                delete filteredValue.min_price;
            }
            if ('max_price' in filteredValue) {
                delete filteredValue.max_price;
            }
        }
    }

    return filteredValue;
};

export const formatCardNumber = (cardNumber, spacing = 4) => {
    const regex = new RegExp(`(\\d{${spacing}})(?=\\d)`, 'g');
    return cardNumber.replace(regex, '$1 ');
};

export const getInitials = (name) => {
    const initials = name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .toUpperCase();
    return initials;
};

export const setNestedValueForUser = (path, value) => {
    const keys = path.split('.');
    const user = getUserFromLocalStorage();
    let ref = user;

    while (keys.length > 1) {
        const key = keys.shift();
        ref = ref[key] = ref[key] || {};
    }

    ref[keys[0]] = value;
    setUser(user);
};

export const updateUserPhoneVerified = (value) => {
    const user = getUserFromLocalStorage();
    if (!Array.isArray(user?.phone)) {
        return;
    }

    const index = user.phone.findIndex((num) => num.isDefault === '1');
    if (index === -1) {
        return;
    }

    user.phone[index].isVerified = value;
    setUser(user);
};

export const slugGeneratorForAdIdWithName = (name, adId) => {
    const slug = name
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

    return `${slug}-${adId}`;
};

export const extractAdIdFromSlug = (slug) => {
    const parts = slug.split('-');
    if (parts.length > 5) {
        return parts.slice(-5).join('-');
    }
    return slug;
};

export const getCategoryName = (categoryId) => {
    const name =
        categoriesAndSubCategories.find((category) => category.id === categoryId)?.name ||
        'vehicle';
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
};

export const getCategoryId = (categorySlug) => {
    if (!Number.isNaN(parseInt(categorySlug))) {
        return categorySlug;
    }
    const category = categoriesAndSubCategories.find(
        (category) => categorySlug === getCategoryName(category.id)
    );
    return category ? category.id : categoriesAndSubCategories[0].id;
};

export const removeParamFromUrl = (url) => url.split('/').slice(0, -1).join('/');

export const capitalizeWords = (string) =>
    string
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

export const joinObjectArrayAndSortByCreatedAt = (array) =>
    array
        .filter((item) => item.created_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

export const numberAbbreviationFormatter = (num) => {
    const formatter = new Intl.NumberFormat('en-US', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 2,
    });
    return formatter.format(num);
};

export const formatMoneyInput = (value) => {
    if (value === '' || value === undefined || value === null) return '';

    const raw = String(value).replace(/[^0-9.]/g, '');
    if (!raw) return '';

    const [intPart, decimalPart] = raw.split('.');
    const formattedInt = new Intl.NumberFormat('en-US').format(Number(intPart || '0'));

    return decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;
};
